/**
 * Gestor de Risco — visão consolidada da saúde de recorrência da base.
 *
 * 3 buckets: Em risco, Evadidos, Em dia.
 * IA sob demanda por cliente ("Sugestões inteligentes") + briefing diário no topo.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, AlertTriangle, TrendingDown, CheckCircle2, Sparkles,
  MessageCircle, Gift, Plus, RefreshCw, Copy,
} from "lucide-react";

const BUCKET_META = {
  em_risco: {
    label: "Em risco",
    icon: AlertTriangle,
    color: "text-amber-600",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    description: "Passaram do prazo sugerido mas ainda são recuperáveis (até +60 dias).",
  },
  evadido: {
    label: "Evadidos",
    icon: TrendingDown,
    color: "text-red-600",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    description: "Mais de 60 dias além do prazo. Reengajamento urgente.",
  },
  em_dia: {
    label: "Em dia",
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    description: "Dentro do intervalo esperado de retorno. Base saudável.",
  },
};

export default function TenantRiskManager() {
  const { slug } = useParams();
  const [overview, setOverview] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [activeBucket, setActiveBucket] = useState("em_risco");
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiClient, setAiClient] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiActions, setAiActions] = useState([]);

  const loadOverview = async () => {
    try {
      const [{ data: ov }, { data: br }] = await Promise.all([
        api.get("/tenant/risk/overview"),
        api.get("/tenant/risk/briefing/today"),
      ]);
      setOverview(ov);
      setBriefing(br.briefing);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadBucket = async (bucket) => {
    setLoadingList(true);
    setActiveBucket(bucket);
    try {
      const { data } = await api.get(`/tenant/risk/list?bucket=${bucket}&limit=200`);
      setList(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadOverview();
    loadBucket("em_risco");
    // eslint-disable-next-line
  }, []);

  const regenerateBriefing = async () => {
    setLoadingBriefing(true);
    try {
      const { data } = await api.post("/tenant/risk/briefing/regenerate");
      setBriefing(data.briefing);
      if (!data.generated) toast.info("Sem clientes em risco/evadidos suficientes para gerar briefing.");
      else toast.success("Briefing atualizado!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingBriefing(false); }
  };

  const openAiSuggestions = async (item) => {
    setAiClient(item);
    setAiOpen(true);
    setAiLoading(true);
    setAiActions([]);
    try {
      const { data } = await api.post(`/tenant/risk/${item.client_id}/suggest`);
      setAiActions(data.actions || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const copyMsg = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Mensagem copiada!");
  };

  const sendWhatsAppMsg = (item, message) => {
    sendWhatsApp({
      phone: item.client?.phone || "",
      text: message,
      clientId: item.client_id,
      context: "risk_manager",
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Clientes</div>
        <h1 className="font-display font-bold text-4xl mt-1">Gestor de Risco</h1>
        <p className="text-muted-foreground mt-2">
          Acompanhe quem está em dia, em risco de evasão ou já evadiu — e receba
          sugestões inteligentes para agir.
        </p>
      </div>

      {/* IA Briefing */}
      <Card
        className="p-5 rounded-tenant border-border mb-6 relative overflow-hidden"
        data-testid="risk-briefing-card"
        style={{
          background: "linear-gradient(135deg, hsl(var(--tenant-primary) / 0.06), transparent)",
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
            >
              <Sparkles className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Briefing IA de hoje
              </div>
              {briefing ? (
                <>
                  <div className="font-display font-semibold text-lg leading-snug" data-testid="briefing-headline">
                    {briefing.headline}
                  </div>
                  {briefing.priority_action && (
                    <div className="text-sm text-foreground/80 mt-1.5" data-testid="briefing-priority">
                      <span className="font-medium">Prioridade: </span>{briefing.priority_action}
                    </div>
                  )}
                  {Array.isArray(briefing.highlights) && briefing.highlights.length > 0 && (
                    <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
                      {briefing.highlights.map((h, i) => (
                        <li key={i}>• {h}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Nenhum briefing gerado hoje. Clique em <span className="font-medium">Gerar agora</span> para consultar a IA.
                </div>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 shrink-0"
            onClick={regenerateBriefing}
            disabled={loadingBriefing}
            data-testid="briefing-regenerate-btn"
          >
            {loadingBriefing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {briefing ? "Atualizar" : "Gerar agora"}
          </Button>
        </div>
      </Card>

      {/* Bucket switcher */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {["em_risco", "evadido", "em_dia"].map((bk) => {
          const meta = BUCKET_META[bk];
          const Icon = meta.icon;
          const count = overview?.counts?.[bk] ?? "—";
          const active = activeBucket === bk;
          return (
            <button
              key={bk}
              onClick={() => loadBucket(bk)}
              data-testid={`risk-bucket-${bk}`}
              className={`p-5 rounded-tenant border text-left transition-all ${
                active
                  ? `${meta.border} ${meta.bg} shadow-sm`
                  : "border-border bg-card hover:border-muted-foreground/40"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${meta.bg}`}>
                  <Icon className={`w-4 h-4 ${meta.color}`} strokeWidth={1.8} />
                </div>
                <div className={`font-display text-3xl font-bold ${active ? meta.color : ""}`}>
                  {count}
                </div>
              </div>
              <div className="font-semibold text-sm">{meta.label}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{meta.description}</div>
            </button>
          );
        })}
      </div>

      {/* Lista do bucket ativo */}
      <Card className="rounded-tenant border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="font-display font-semibold">
            {BUCKET_META[activeBucket].label} ({list.length})
          </div>
        </div>

        {loadingList ? (
          <div className="p-10 text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline" />
          </div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            Nenhum cliente neste bucket 🎉
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {list.map((it) => (
              <li
                key={it.client_id}
                data-testid={`risk-row-${it.client_id}`}
                className="px-5 py-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{it.client?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {it.last_service_name || "sem serviço"} · há{" "}
                    <span className={activeBucket !== "em_dia" ? "text-foreground font-medium" : ""}>
                      {it.days_since_last} dias
                    </span>
                    {" "}· sugestão a cada {it.return_after_days}d
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="font-mono text-xs">
                    {it.client?.points_balance ?? 0} pts
                  </Badge>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => openAiSuggestions(it)}
                    data-testid={`risk-suggest-btn-${it.client_id}`}
                    style={{
                      background: `hsl(var(--tenant-primary))`,
                      color: `hsl(var(--tenant-primary-foreground))`,
                    }}
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Sugerir ações
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Diálogo IA de sugestões */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-2xl" data-testid="ai-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Sugestões da IA para {aiClient?.client?.name}
            </DialogTitle>
            <DialogDescription>
              Ações geradas com base no histórico, frequência e catálogo desta empresa.
            </DialogDescription>
          </DialogHeader>

          {aiLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Consultando IA...
            </div>
          ) : (
            <div className="space-y-3">
              {aiActions.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  Nenhuma sugestão retornada.
                </div>
              ) : (
                aiActions.map((a, i) => (
                  <Card
                    key={i}
                    className="p-4 border-border rounded-lg"
                    data-testid={`ai-action-${a.type}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        {a.type === "call_back" && <MessageCircle className="w-4 h-4 text-blue-600" />}
                        {a.type === "bonus_points" && <Gift className="w-4 h-4 text-amber-600" />}
                        {a.type === "cross_sell" && <Plus className="w-4 h-4 text-emerald-600" />}
                        <div className="font-medium text-sm">{a.title}</div>
                      </div>
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {a.type === "call_back" && "chamar de volta"}
                        {a.type === "bonus_points" && `+${a.points_bonus || 0} pts`}
                        {a.type === "cross_sell" && "cross-sell"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{a.why}</p>
                    <div className="bg-muted/40 rounded-md p-3 text-sm italic mb-3 whitespace-pre-wrap">
                      &ldquo;{a.message}&rdquo;
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => copyMsg(a.message)}
                        data-testid={`ai-copy-${i}`}
                      >
                        <Copy className="w-3.5 h-3.5" /> Copiar
                      </Button>
                      <Button
                        size="sm"
                        className="gap-1.5"
                        onClick={() => sendWhatsAppMsg(aiClient, a.message)}
                        data-testid={`ai-send-wa-${i}`}
                        style={{
                          background: `hsl(var(--tenant-primary))`,
                          color: `hsl(var(--tenant-primary-foreground))`,
                        }}
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> Enviar WhatsApp
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
